import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import BikeImageAnalyzer from './components/BikeImageAnalyzer';
import UpgradeRecommendationCard from './components/UpgradeRecommendationCard';
import RecommendationFilters from './components/RecommendationFilters';
import AnalysisSummaryPanel from './components/AnalysisSummaryPanel';
import SocialSharePanel from './components/SocialSharePanel';

const BikeAnalysisResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [filteredRecommendations, setFilteredRecommendations] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});

  // Mock analysis data - in real app this would come from API/state
  const analysisData = {
    bikeModel: "Trek Domane AL 2",
    analysisDate: "2025-01-05",
    bikeImage: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop",
    totalComponents: 12,
    upgradeableComponents: 6,
    goodComponents: 6,
    totalUpgradeCost: 3487,
    estimatedPerformanceGain: 23,
    compatibilityScore: 92
  };

  const mockRecommendations = [
    {
      id: 1,
      component: "Front & Rear Wheels",
      category: "Wheels & Tires",
      priority: "high",
      current: {
        name: "Basic Aluminum Wheelset",
        specs: "32-spoke, rim brake compatible",
        image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=100&h=100&fit=crop"
      },
      recommended: {
        name: "Carbon Fiber Wheelset",
        specs: "Tubeless ready, disc brake",
        image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=100&h=100&fit=crop"
      },
      benefits: [
        "30% weight reduction",
        "Improved aerodynamics",
        "Better stiffness and power transfer",
        "Tubeless tire compatibility"
      ],
      cost: 1299,
      priceRange: { min: 899, max: 1599 },
      difficulty: "medium",
      installTime: "2-3 hours",
      compatibility: "perfect",
      vendorCount: 8,
      avgRating: 4.7,
      isWishlisted: false
    },
    {
      id: 2,
      component: "Drivetrain Groupset",
      category: "Drivetrain",
      priority: "high",
      current: {
        name: "Shimano 105 R7000",
        specs: "11-speed, mechanical shifting",
        image: "https://images.unsplash.com/photo-1544191696-15693072b5a8?w=100&h=100&fit=crop"
      },
      recommended: {
        name: "Shimano Ultegra R8000",
        specs: "11-speed, smoother operation",
        image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=100&h=100&fit=crop"
      },
      benefits: [
        "Smoother and faster shifting",
        "Better durability and longevity",
        "15% weight reduction",
        "Improved gear ratios"
      ],
      cost: 899,
      priceRange: { min: 799, max: 999 },
      difficulty: "hard",
      installTime: "4-6 hours",
      compatibility: "good",
      vendorCount: 12,
      avgRating: 4.8,
      isWishlisted: true
    },
    {
      id: 3,
      component: "Saddle",
      category: "Comfort",
      priority: "medium",
      current: {
        name: "Basic Comfort Saddle",
        specs: "Standard padding, steel rails",
        image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=100&h=100&fit=crop"
      },
      recommended: {
        name: "Fizik Antares R3",
        specs: "Carbon rails, ergonomic design",
        image: "https://images.unsplash.com/photo-1544191696-15693072b5a8?w=100&h=100&fit=crop"
      },
      benefits: [
        "Better pressure distribution",
        "Reduced weight",
        "Improved comfort on long rides",
        "Better power transfer"
      ],
      cost: 189,
      priceRange: { min: 149, max: 229 },
      difficulty: "easy",
      installTime: "15 minutes",
      compatibility: "perfect",
      vendorCount: 6,
      avgRating: 4.5,
      isWishlisted: false
    },
    {
      id: 4,
      component: "Pedals",
      category: "Drivetrain",
      priority: "low",
      current: {
        name: "Platform Pedals",
        specs: "Basic aluminum platform",
        image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=100&h=100&fit=crop"
      },
      recommended: {
        name: "Shimano SPD-SL Pedals",
        specs: "Clipless, carbon composite",
        image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=100&h=100&fit=crop"
      },
      benefits: [
        "Better power transfer efficiency",
        "Improved pedaling technique",
        "Secure foot connection",
        "Reduced fatigue"
      ],
      cost: 149,
      priceRange: { min: 99, max: 199 },
      difficulty: "easy",
      installTime: "30 minutes",
      compatibility: "perfect",
      vendorCount: 10,
      avgRating: 4.6,
      isWishlisted: false
    },
    {
      id: 5,
      component: "Tires",
      category: "Wheels & Tires",
      priority: "medium",
      current: {
        name: "Basic Road Tires",
        specs: "25mm, basic compound",
        image: "https://images.unsplash.com/photo-1544191696-15693072b5a8?w=100&h=100&fit=crop"
      },
      recommended: {
        name: "Continental GP5000",
        specs: "28mm, tubeless ready",
        image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=100&h=100&fit=crop"
      },
      benefits: [
        "Lower rolling resistance",
        "Better puncture protection",
        "Improved grip and handling",
        "Tubeless compatibility"
      ],
      cost: 89,
      priceRange: { min: 69, max: 109 },
      difficulty: "easy",
      installTime: "45 minutes",
      compatibility: "good",
      vendorCount: 15,
      avgRating: 4.9,
      isWishlisted: false
    },
    {
      id: 6,
      component: "Bar Tape",
      category: "Cockpit",
      priority: "low",
      current: {
        name: "Basic Bar Tape",
        specs: "Standard foam padding",
        image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=100&h=100&fit=crop"
      },
      recommended: {
        name: "Fizik Vento Solocush",
        specs: "Gel padding, tacky surface",
        image: "https://images.unsplash.com/photo-1544191696-15693072b5a8?w=100&h=100&fit=crop"
      },
      benefits: [
        "Better grip and comfort",
        "Vibration dampening",
        "Improved aesthetics",
        "Weather resistance"
      ],
      cost: 35,
      priceRange: { min: 25, max: 45 },
      difficulty: "easy",
      installTime: "1 hour",
      compatibility: "perfect",
      vendorCount: 8,
      avgRating: 4.3,
      isWishlisted: false
    }
  ];

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    // Simulate loading analysis results
    const timer = setTimeout(() => {
      setLoading(false);
      setFilteredRecommendations(mockRecommendations);
    }, 1500);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleComponentSelect = (component, action = 'select') => {
    setSelectedComponent(component);
    
    if (action === 'view-details') {
      // Navigate to detailed component view
      navigate(`/upgrade-recommendations?component=${component?.id}`);
    } else if (action === 'add-to-wishlist') {
      // Handle wishlist toggle
      console.log('Toggle wishlist for:', component);
    } else if (action === 'share') {
      // Handle sharing
      console.log('Share analysis');
    } else if (action === 'download') {
      // Handle download
      console.log('Download report');
    } else if (action === 'new-analysis') {
      navigate('/photo-upload');
    }
  };

  const handleRecommendationAction = (recommendation, action) => {
    if (action === 'view-options') {
      navigate('/upgrade-recommendations', { 
        state: { selectedRecommendation: recommendation } 
      });
    } else if (action === 'add-to-wishlist') {
      navigate('/wishlist');
    } else if (action === 'view-details') {
      setSelectedComponent(recommendation);
    }
  };

  const handleFiltersChange = (filters) => {
    setActiveFilters(filters);
    
    let filtered = [...mockRecommendations];
    
    // Apply priority filter
    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(r => r?.priority === filters?.priority);
    }
    
    // Apply price range filter
    if (filters?.priceRange !== 'all') {
      switch (filters?.priceRange) {
        case 'under-100':
          filtered = filtered?.filter(r => r?.cost < 100);
          break;
        case '100-500':
          filtered = filtered?.filter(r => r?.cost >= 100 && r?.cost <= 500);
          break;
        case '500-1000':
          filtered = filtered?.filter(r => r?.cost >= 500 && r?.cost <= 1000);
          break;
        case 'over-1000':
          filtered = filtered?.filter(r => r?.cost > 1000);
          break;
      }
    }
    
    // Apply difficulty filter
    if (filters?.difficulty !== 'all') {
      filtered = filtered?.filter(r => r?.difficulty === filters?.difficulty);
    }
    
    // Apply compatibility filter
    if (filters?.compatibility !== 'all') {
      if (filters?.compatibility === 'adapter') {
        filtered = filtered?.filter(r => r?.compatibility === 'requires-adapter');
      } else {
        filtered = filtered?.filter(r => r?.compatibility === filters?.compatibility);
      }
    }
    
    // Apply sorting
    switch (filters?.sortBy) {
      case 'cost-low':
        filtered?.sort((a, b) => a?.cost - b?.cost);
        break;
      case 'cost-high':
        filtered?.sort((a, b) => b?.cost - a?.cost);
        break;
      case 'difficulty':
        const difficultyOrder = { easy: 1, medium: 2, hard: 3 };
        filtered?.sort((a, b) => difficultyOrder?.[a?.difficulty] - difficultyOrder?.[b?.difficulty]);
        break;
      case 'compatibility':
        const compatOrder = { perfect: 1, good: 2, 'requires-adapter': 3 };
        filtered?.sort((a, b) => compatOrder?.[a?.compatibility] - compatOrder?.[b?.compatibility]);
        break;
      default: // priority
        const priorityOrder = { high: 1, medium: 2, low: 3 };
        filtered?.sort((a, b) => priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority]);
    }
    
    setFilteredRecommendations(filtered);
  };

  const handleShareAnalysis = (type, platform) => {
    console.log(`Shared via ${type}:`, platform);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Loader2" size={32} color="var(--color-accent)" className="animate-spin" />
                </div>
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  Analyzing Your Bike
                </h2>
                <p className="text-muted-foreground">
                  Processing components and generating recommendations...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-2">
              <button 
                onClick={() => navigate('/user-dashboard')}
                className="hover:text-accent transition-colors"
              >
                Dashboard
              </button>
              <Icon name="ChevronRight" size={16} />
              <span>Analysis Results</span>
            </div>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Bike Analysis Results
                </h1>
                <p className="text-muted-foreground">
                  AI-powered component analysis and upgrade recommendations for your {analysisData?.bikeModel}
                </p>
              </div>
              <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/photo-upload')}
                  iconName="Camera"
                  iconPosition="left"
                >
                  New Analysis
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate('/local-shop-finder')}
                  iconName="MapPin"
                  iconPosition="left"
                >
                  Find Shops
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Image Analysis */}
            <div className="xl:col-span-2 space-y-6">
              {/* Bike Image Analyzer */}
              <BikeImageAnalyzer
                bikeImage={analysisData?.bikeImage}
                analysisData={analysisData}
                onComponentSelect={handleComponentSelect}
              />

              {/* Filters */}
              <RecommendationFilters
                onFiltersChange={handleFiltersChange}
                totalRecommendations={filteredRecommendations?.length}
              />

              {/* Recommendations List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-foreground">
                    Upgrade Recommendations ({filteredRecommendations?.length})
                  </h2>
                  {filteredRecommendations?.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/upgrade-recommendations')}
                      iconName="ExternalLink"
                      iconPosition="right"
                    >
                      View All Options
                    </Button>
                  )}
                </div>

                {filteredRecommendations?.length === 0 ? (
                  <div className="text-center py-12 bg-card border border-border rounded-lg">
                    <Icon name="Filter" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No recommendations match your filters</h3>
                    <p className="text-muted-foreground mb-4">Try adjusting your filter criteria to see more options</p>
                    <Button
                      variant="outline"
                      onClick={() => handleFiltersChange({ sortBy: 'priority', priority: 'all', priceRange: 'all', difficulty: 'all', compatibility: 'all' })}
                    >
                      Clear Filters
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredRecommendations?.map((recommendation) => (
                      <UpgradeRecommendationCard
                        key={recommendation?.id}
                        recommendation={recommendation}
                        onViewOptions={(rec) => handleRecommendationAction(rec, 'view-options')}
                        onAddToWishlist={(rec) => handleRecommendationAction(rec, 'add-to-wishlist')}
                        onViewDetails={(rec) => handleRecommendationAction(rec, 'view-details')}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Summary & Actions */}
            <div className="space-y-6">
              {/* Analysis Summary */}
              <AnalysisSummaryPanel
                analysisData={analysisData}
                onShareAnalysis={() => handleShareAnalysis('summary', 'share')}
                onDownloadReport={() => handleShareAnalysis('summary', 'download')}
                onViewAllRecommendations={() => navigate('/upgrade-recommendations')}
              />

              {/* Social Share */}
              <SocialSharePanel
                analysisData={analysisData}
                bikeImage={analysisData?.bikeImage}
                onShare={handleShareAnalysis}
              />

              {/* Quick Actions */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/wishlist')}
                    iconName="Heart"
                    iconPosition="left"
                    fullWidth
                  >
                    View Wishlist
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/local-shop-finder')}
                    iconName="MapPin"
                    iconPosition="left"
                    fullWidth
                  >
                    Find Local Shops
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/user-dashboard')}
                    iconName="LayoutDashboard"
                    iconPosition="left"
                    fullWidth
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BikeAnalysisResults;